import { Component, Input, Output, EventEmitter, ViewChild, ElementRef, AfterViewInit, OnDestroy, Inject, PLATFORM_ID, NgZone } from '@angular/core';
import { CommonModule, isPlatformBrowser, DOCUMENT } from '@angular/common';
import { RadarService } from '../../services/radar/radar.service';

@Component({
  selector: 'ui-radar-map',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './radar-map.component.html',
  styleUrl: './radar-map.component.scss',
})
export class RadarMapComponent implements AfterViewInit, OnDestroy {
  // Private backing field for center
  private _center: { lat: number; lng: number } = { lat: 37.7749, lng: -122.4194 };
  
  @Input()
  set center(value: { lat: number; lng: number }) {
    this._center = value;
    const hasValidCenter =
      !!value &&
      typeof value.lat === 'number' &&
      typeof value.lng === 'number';
    // If map is not initialized yet and we now have valid center, try to initialize
    if (!this.radarMap && hasValidCenter && this.radarMapContainer) {
      this.initializeRadarMap();
    } else if(this.radarMap && hasValidCenter && typeof this.radarMap.setCenter === 'function') {
      // If map already exists and center changes, update the map center to keep it in sync
      this.ngZone.run(() => {
        this.radarMap.setCenter([value.lng, value.lat]);
      });
    }
  }
  get center(): { lat: number; lng: number } {
    return this._center;
  }
  
  private _zoom: number = 14;
  /* Mirrors `center` above: a zoom set after the map exists is applied to the live map
     rather than silently ignored until the next re-create. Callers that bind a constant
     zoom are unaffected -- the value is still what the map is built with. */
  @Input()
  set zoom(value: number) {
    this._zoom = value;
    if (this.radarMap && typeof this.radarMap.setZoom === 'function' && Number.isFinite(value)) {
      const current = typeof this.radarMap.getZoom === 'function' ? this.radarMap.getZoom() : null;
      if (current === null || Math.abs(current - value) > 0.01) {
        this.ngZone.run(() => {
          this.radarMap.setZoom(value);
        });
      }
    }
  }
  get zoom(): number {
    return this._zoom;
  }
  private _centerMarkerContent?: HTMLElement;
  @Input()
  set centerMarkerContent(value: HTMLElement | undefined) {
    this._centerMarkerContent = value;
    this.tryUpdateMarkers();
  }
  get centerMarkerContent(): HTMLElement | undefined {
    return this._centerMarkerContent;
  }
  @Input() radarStyle?: string;  // Optional - if not provided, uses style from RadarService config
  @Input() pageType: string = 'parking';
  @Input() showInfoBoxOnMobile: boolean = false;  // Default to false for backward compatibility

  /** Show/hide map controls (e.g. fullscreen). Default true. */
  @Input() showMapControls: boolean = true;
  /** When false, mouse wheel scrolls the page instead of zooming the map. Default true. */
  @Input() scrollZoom: boolean = true;
  /** When false, the map does not capture scroll, drag, or zoom. Marker clicks still work. Default true. */
  @Input() interactive: boolean = true;
  /** Show/hide fullscreen button. Default true. */
  @Input() showFullscreenControl: boolean = true;
  /* MapLibre's +/- buttons, which the Radar SDK adds by default. Opt out per page:
     every existing caller keeps them unless it passes false. */
  @Input() showZoomControls: boolean = true;
  /** Fullscreen icon URL. Defaults to assets/images/map-fullscreen.svg (app must provide asset). */
  @Input() fullscreenIconUrl: string = 'assets/images/map-fullscreen.svg';
  /** Exit fullscreen icon URL. Defaults to assets/images/map-not-fullscreen.svg (app must provide asset). */
  @Input() exitFullscreenIconUrl: string = 'assets/images/map-not-fullscreen.svg';

  // Private backing fields for properties with timing issues
  private _markers: any[] = [];
  private _nearbyMarkerElements: { [id: number]: HTMLElement } = {};
  private _activeMarkerId: any;
  
  // Getter/Setter for markers to handle timing issue
  @Input()
  set markers(value: any[]) {
    this._markers = value;
    this.tryUpdateMarkers();
  }
  get markers(): any[] {
    return this._markers;
  }

  // Getter/Setter for nearbyMarkerElements to handle timing issue
  @Input()
  set nearbyMarkerElements(value: { [id: number | string]: HTMLElement }) {
    this._nearbyMarkerElements = value;
    this.tryUpdateMarkers();
  }
  get nearbyMarkerElements(): { [id: number | string]: HTMLElement } {
    return this._nearbyMarkerElements;
  }

  // Getter/Setter for activeMarkerId to handle active marker updates (styling/highlight)
  @Input()
  set activeMarkerId(value: any) {
    this._activeMarkerId = value;
    this.updateActiveMarker();
  }
  get activeMarkerId(): any {
    return this._activeMarkerId;
  }

  /**
   * Optional. When set, only this marker's infobox is shown (e.g. from map click).
   * When not set, infobox follows activeMarkerId (legacy behavior).
   * Use this to avoid opening infobox on list hover when activeMarkerId is used for highlight only.
   */
  @Input()
  set showInfoBoxForMarkerId(value: any) {
    this._showInfoBoxForMarkerId = value;
    this.updateActiveMarker();
  }
  get showInfoBoxForMarkerId(): any {
    return this._showInfoBoxForMarkerId;
  }
  private _showInfoBoxForMarkerId: any;

  /* When false, the infobox follows `showInfoBoxForMarkerId` alone and never falls back
     to `activeMarkerId`. Pages that drive `activeMarkerId` from list hover need this:
     the fallback below treats an empty `showInfoBoxForMarkerId` as "not provided", so
     without it every hover would pop an infobox open. Defaults to the original
     fallback so existing callers are unaffected. */
  @Input() infoBoxFollowsActiveMarker: boolean = true;

  /** Where the host's stylesheet puts an open infobox relative to its marker.
      `over` (default) is the original layout, centred above the pin, and only ever
      gets the edge clamp. `beside` means the host anchors the card to the pin's right
      and provides a `.place-left` variant anchored to its left; this component then
      picks the side with room on every open and every map move, before clamping.
      Auto-repair only: it is honoured solely when `pageType` is `autorepair`, so no
      other vertical can pick it up by accident. */
  @Input() infoBoxPlacement: 'over' | 'beside' = 'over';

  @Output() mapReady = new EventEmitter<any>();
  @Output() markerClick = new EventEmitter<any>();
  @Output() markerHover = new EventEmitter<any>();
  @Output() markerHoverLeave = new EventEmitter<void>();
  @Output() infoboxClose = new EventEmitter<void>();
  @Output() infoBoxClick = new EventEmitter<any>();
  @Output() autoRepairBookNowBtnClick = new EventEmitter<number>();
  @Output() autoRepairFavouriteBtnClick = new EventEmitter<number>();
  
  @ViewChild('radarMapContainer', { static: false }) radarMapContainer?: ElementRef;
  @ViewChild('mapWrapper', { static: false }) mapWrapper?: ElementRef;

  radarMap: any;
  radarMarkers: any[] = [];
  isBrowser: boolean;
  isMapReady: boolean = false;
  /** Whether the map is in fullscreen */
  isFullscreen: boolean = false;

  constructor(
    @Inject(PLATFORM_ID) private platformId: any,
    @Inject(DOCUMENT) private document: Document,
    private radarService: RadarService,
    private ngZone: NgZone
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  /** Toggle fullscreen on the map wrapper */
  toggleFullscreen(): void {
    if (!this.isBrowser || !this.mapWrapper?.nativeElement) return;
    const el = this.mapWrapper.nativeElement as HTMLElement;
    if (!this.isFullscreen) {
      if (el.requestFullscreen) {
        el.requestFullscreen().then(() => {
          this.ngZone.run(() => (this.isFullscreen = true));
        }).catch(() => {});
      }
    } else {
      if (this.document.exitFullscreen) {
        this.document.exitFullscreen().then(() => {
          this.ngZone.run(() => (this.isFullscreen = false));
        }).catch(() => {});
      }
    }
  }

  private fullscreenChangeListener = () => {
    this.ngZone.run(() => {
      this.isFullscreen = !!this.document.fullscreenElement;
    });
  };

  ngAfterViewInit() {
    if (this.isBrowser) {
      this.document.addEventListener('fullscreenchange', this.fullscreenChangeListener);
      this.ngZone.runOutsideAngular(() => {
        setTimeout(() => {
          if (!this.radarMap) {
            this.initializeRadarMap();
          }
        }, 100);
      });
    }
  }

  private initializeRadarMap() {
    if (!this.radarMapContainer) {
      console.error('Radar map container not found');
      return;
    }

    if (!this.center || typeof this.center.lat !== 'number' || typeof this.center.lng !== 'number') {
      console.warn('Center coordinates not ready yet, skipping initialization');
      return;
    }

    const Radar = this.radarService.getRadar();
    
    if (!Radar) {
      console.error('Radar SDK not loaded');
      return;
    }
    if (!Radar.ui || typeof Radar.ui.map !== 'function') {
      console.error('Radar SDK map UI not available. Ensure Radar.initialize(publishableKey) was called and the SDK has loaded.');
      return;
    }

    try {
      // Use component's radarStyle input if provided, otherwise fall back to RadarService config, then to default
      const mapStyle = this.radarStyle || this.radarService.getMapStyle() || 'radar-default-v1';
      
      // Create Radar map
      this.radarMap = Radar.ui.map({
        container: this.radarMapContainer.nativeElement,
        style: mapStyle,
        center: [this.center.lng, this.center.lat],
        zoom: this.zoom || 14,
        showZoomControls: this.showZoomControls,
        scrollZoom: this.scrollZoom,
        interactive: this.interactive
      });
      if (!this.scrollZoom) {
        this.passWheelToPage(this.radarMapContainer.nativeElement);
      }

      // `load` fires when the style JSON is parsed; sprites/images may still be loading.
      // MapLibre can throw `this.style.imageManager` null if markers/DOM attach before the first full render.
      // Wait for `idle` (first frame with style/renderer fully ready), same pattern as Google map idle gate.
      this.ngZone.runOutsideAngular(() => {
        for (const ev of ['move', 'moveend', 'zoom', 'zoomend', 'resize']) {
          this.radarMap.on?.(ev, this.repositionOpenInfoBox);
        }
      });

      this.radarMap.on('load', () => {
        const finishWhenStable = () => {
          if (this.isMapReady) {
            return;
          }
          if (!this.scrollZoom && this.radarMap?.scrollZoom?.disable) {
            this.radarMap.scrollZoom.disable();
          }
          this.isMapReady = true;
          this.ngZone.run(() => {
            this.mapReady.emit(this.radarMap);
            this.tryUpdateMarkers();
          });
        };

        const mapAny = this.radarMap as {
          once?: (ev: string, fn: () => void) => void;
          isStyleLoaded?: () => boolean;
        };
        if (typeof mapAny.once === 'function') {
          mapAny.once('idle', finishWhenStable);
        } else {
          this.radarMap.on?.('idle', finishWhenStable);
        }

        setTimeout(() => {
          if (!this.isMapReady) {
            finishWhenStable();
          }
        }, 5000);
      });

    } catch (error) {
      console.error('Error initializing Radar map:', error);
    }
  }

  /**
   * Attempts to update markers only when ALL conditions are met:
   * - Map is ready
   * - Markers data exists
   * - Marker DOM elements exist
   * This prevents race conditions between async operations
   */
  private tryUpdateMarkers() {
    const hasMarkers = this._markers && this._markers.length > 0;
    const hasMarkerElements = Object.keys(this._nearbyMarkerElements).length > 0;
    if (this.isMapReady && hasMarkers && hasMarkerElements) {
      this.updateMarkers();
    }
  }

  updateMarkers() {
    if (!this.radarMap || !this.isMapReady) {
      return;
    }

    // Guard: MapLibre style must exist before marker layers use sprite/image pipeline (imageManager).
    const mapAny = this.radarMap as { style?: unknown; isStyleLoaded?: () => boolean };
    if (mapAny.style == null) {
      const m = this.radarMap as { once?: (ev: string, fn: () => void) => void };
      if (typeof m.once === 'function') {
        m.once('idle', () => this.ngZone.run(() => this.updateMarkers()));
      } else {
        setTimeout(() => this.ngZone.run(() => this.updateMarkers()), 100);
      }
      return;
    }
    if (typeof mapAny.isStyleLoaded === 'function' && !mapAny.isStyleLoaded()) {
      const m = this.radarMap as { once?: (ev: string, fn: () => void) => void };
      if (typeof m.once === 'function') {
        m.once('idle', () => this.ngZone.run(() => this.updateMarkers()));
      } else {
        setTimeout(() => this.ngZone.run(() => this.updateMarkers()), 100);
      }
      return;
    }

    const Radar = this.radarService.getRadar();
    
    // Clear existing markers
    this.clearMarkers();

    // Add center marker
    if (this._centerMarkerContent) {
      const centerMarkerClone = this._centerMarkerContent.cloneNode(true) as HTMLElement;

      const centerMarker = (Radar.ui.marker({
        element: centerMarkerClone,
        anchor: 'center'
      } as Record<string, unknown>) as { setLngLat: (lngLat: [number, number]) => { addTo: (map: unknown) => unknown }; addTo: (map: unknown) => unknown })
        .setLngLat([this.center.lng, this.center.lat])
        .addTo(this.radarMap);
      
      this.radarMarkers.push(centerMarker);
    }

    // Add nearby markers
    this.markers.forEach((marker) => {
      // Radar expects [lng, lat]
      const lng = marker.position[1];
      const lat = marker.position[0];
      const position: [number, number] = [lng, lat];

      // Use the marker content from nearbyMarkerElements (provided by parent)
      const markerElement = this.nearbyMarkerElements[marker.id];
      
      if (!markerElement) {
        return;
      }

      // Clone the element to avoid DOM manipulation issues
      const markerElementClone = markerElement.cloneNode(true) as HTMLElement;

      const radarMarker = (Radar.ui.marker({
        element: markerElementClone,
        anchor: 'center'
      } as Record<string, unknown>) as { setLngLat: (lngLat: [number, number]) => { addTo: (map: unknown) => unknown }; addTo: (map: unknown) => unknown })
        .setLngLat(position)
        .addTo(this.radarMap);

      // Add click event to the cloned element
      markerElementClone.addEventListener('click', () => {
        this.ngZone.run(() => {
          this.markerClick.emit(marker);
        });
      });

      if(this.pageType == 'parking') {
        const infoBox = markerElementClone.querySelector('.infobx');
        if(infoBox) {
          infoBox.addEventListener('click', (event) => {
            event.stopPropagation();
            this.ngZone.run(() => {
              this.infoBoxClick.emit(marker);
            });
          });
        }
        const closeBtn = markerElementClone.querySelector('.infobx button[aria-label="close"]');
        if (closeBtn) {
          closeBtn.addEventListener('click', (event) => {
            event.stopPropagation();
            this.ngZone.run(() => {
              if (infoBox) {
                infoBox.classList.remove('showinfo');
              }
              this.infoboxClose.emit();
            });
          });
        }
      } else if(this.pageType == 'gas') {
        const gasMarkerCntr = markerElementClone.querySelector('.gas-marker-cntr') || markerElementClone;
        const gasMarkerEl = markerElementClone.querySelector('.gas-marker');
        if (gasMarkerEl) {
          gasMarkerEl.addEventListener('mouseenter', () => {
            this.ngZone.run(() => {
              this.markerHover.emit(marker);
            });
          });
        }
        gasMarkerCntr.addEventListener('mouseleave', () => {
          this.ngZone.run(() => {
            this.markerHoverLeave.emit();
          });
        });
        const infoBox = markerElementClone.querySelector('.infobx');
        if(infoBox) {
          infoBox.addEventListener('click', (event) => {
            event.stopPropagation();
            this.ngZone.run(() => {
              this.infoBoxClick.emit(marker);
            });
          });
        }
        const closeBtn = markerElementClone.querySelector('.infobx button[aria-label="close"]');
        if (closeBtn) {
          closeBtn.addEventListener('click', (event) => {
            event.stopPropagation();
            this.ngZone.run(() => {
              if (infoBox) {
                infoBox.classList.remove('showinfo');
              }
              this.infoboxClose.emit();
            });
          });
        }
      } else if(this.pageType == 'autorepair') {
        // cloneNode copies structure but not listeners, so every Angular (click) the
        // card template declares is dead on the copy that reaches the map. Re-bind each
        // control here; the inner stopPropagation calls also keep the whole-clone
        // markerClick listener above from firing alongside them.
        const infoBox = markerElementClone.querySelector('.infobx');
        const infoBoxBookNowBtn = markerElementClone.querySelector('.infobx button.book-now-btn');
        if(infoBoxBookNowBtn) {
          infoBoxBookNowBtn.addEventListener('click', (event) => {
            event.stopPropagation();
            this.ngZone.run(() => {
              this.autoRepairBookNowBtnClick.emit(marker.listingId);
            });
          });
        }
        const favouriteBtn = markerElementClone.querySelector('.infobx button.favorite-btn');
        if (favouriteBtn) {
          favouriteBtn.addEventListener('click', (event) => {
            event.stopPropagation();
            this.ngZone.run(() => {
              this.autoRepairFavouriteBtnClick.emit(marker.listingId);
            });
          });
        }
        const closeBtn = markerElementClone.querySelector('.infobx button[aria-label="close"]');
        if (closeBtn) {
          closeBtn.addEventListener('click', (event) => {
            event.stopPropagation();
            this.ngZone.run(() => {
              // Hide it here too: the host clears showPopupId, but the card is a clone
              // and only updateActiveMarker() reaches it.
              if (infoBox) {
                infoBox.classList.remove('showinfo');
              }
              this.infoboxClose.emit();
            });
          });
        }
        // Everything above the button row opens the shop, matching the listing card.
        const cardTopSection = markerElementClone.querySelector('.infobx .card-top-section');
        if (cardTopSection) {
          cardTopSection.addEventListener('click', (event) => {
            event.stopPropagation();
            this.ngZone.run(() => {
              this.infoBoxClick.emit(marker);
            });
          });
        }
      } else if(this.pageType == 'city-parking') {
        // Add click event to close button in infobox
        const closeBtn = markerElementClone.querySelector('.infobx .popimg button[aria-label="close"]');
        if (closeBtn) {
          closeBtn.addEventListener('click', (event) => {
            event.stopPropagation();
            this.ngZone.run(() => {
              this.infoboxClose.emit();
            });
          });
        }
        // Add click event to infobox for navigation to details page
        const infoBoxBtn = markerElementClone.querySelector('.infobx .popcont .rtesr .book-now');
        if(infoBoxBtn) {
          infoBoxBtn.addEventListener('click', (event) => {
            event.stopPropagation();
            this.ngZone.run(() => {
              this.infoBoxClick.emit(marker);
            });
          });
        }
      }
      
      this.radarMarkers.push(radarMarker);
    });
  }

  /**
   * Repaint one marker's favourite control. The cards on the map are clones taken when
   * the markers were built, so a later change to the source template never reaches them.
   * The host calls this once its favourite request has actually succeeded, rather than
   * toggling optimistically and leaving the heart wrong when the call fails.
   */
  setMarkerFavourite(listingId: number | string, isFavourite: boolean): void {
    if (!this.isBrowser || !this.radarMapContainer) return;
    const config = this.getMarkerConfig(this.pageType);
    const markerContainer = this.radarMapContainer.nativeElement
      .querySelector(`${config.markerIdPrefix}${listingId}`)
      ?.closest(config.containerSelector);
    const favouriteBtn = markerContainer?.querySelector('.infobx button.favorite-btn') as HTMLElement | null;
    if (!favouriteBtn) return;
    favouriteBtn.classList.toggle('active', isFavourite);
    const label = isFavourite ? 'Remove from favorites' : 'Add to favorites';
    favouriteBtn.setAttribute('title', label);
    const srOnlyLabel = favouriteBtn.querySelector('.sr-only');
    if (srOnlyLabel) {
      srOnlyLabel.textContent = label;
    }
  }

  private clearMarkers() {
    if (this.radarMarkers.length > 0) {
      this.radarMarkers.forEach(marker => {
        try {
          marker.remove();
        } catch (e) {
          console.error('Error removing marker:', e);
        }
      });
      this.radarMarkers = [];
    }
  }

  /**
   * Updates the active marker styling and infobox visibility based on activeMarkerId
   */
  private updateActiveMarker() {
    if (!this.radarMapContainer) return;
    
    // Get CSS selectors based on page type
    const config = this.getMarkerConfig(this.pageType);
    
    // Remove active classes from all markers and hide all infoboxes
    const allMarkers = this.radarMapContainer.nativeElement.querySelectorAll(config.containerSelector);
    allMarkers.forEach((marker: Element) => {
      marker.classList.remove(config.containerActiveClass);
      const markerElement = marker.querySelector(config.markerSelector);
      if (markerElement) {
        markerElement.classList.remove(config.markerActiveClass);
      }
      
      // Hide infobox
      const infobox = marker.querySelector('.infobx') as HTMLElement | null;
      if (infobox) {
        infobox.classList.remove('showinfo');
        // Drop any edge nudge from a previous open, so the next one measures
        // the stylesheet's offset rather than a stale corrected position.
        infobox.style.left = '';
        infobox.style.top = '';
      }
    });
    
    // Add active class to current marker (for highlight on hover/click)
    if (this.activeMarkerId !== null && this.activeMarkerId !== undefined) {
      const activeMarker = this.radarMapContainer.nativeElement
        .querySelector(`${config.markerIdPrefix}${this.activeMarkerId}`)
        ?.closest(config.containerSelector);
      
      if (activeMarker) {
        activeMarker.classList.add(config.containerActiveClass);
        const markerElement = activeMarker.querySelector(config.markerSelector);
        if (markerElement) {
          markerElement.classList.add(config.markerActiveClass);
        }
      }
    }

    // Show infobox only for showInfoBoxForMarkerId when provided (e.g. map click), else for activeMarkerId (legacy)
    const hasExplicitInfoboxId = this.showInfoBoxForMarkerId !== undefined && this.showInfoBoxForMarkerId !== null && this.showInfoBoxForMarkerId !== '';
    const infoboxMarkerId = hasExplicitInfoboxId
      ? this.showInfoBoxForMarkerId
      : (this.infoBoxFollowsActiveMarker ? this.activeMarkerId : null);
    const hasValidInfoboxId = infoboxMarkerId !== null && infoboxMarkerId !== undefined && infoboxMarkerId !== '';
    if (hasValidInfoboxId && this.isBrowser && (window.innerWidth > 767 || this.showInfoBoxOnMobile)) {
      const infoboxMarker = this.radarMapContainer.nativeElement
        .querySelector(`${config.markerIdPrefix}${infoboxMarkerId}`)
        ?.closest(config.containerSelector);
      if (infoboxMarker) {
        const infobox = infoboxMarker.querySelector('.infobx');
        if (infobox) {
          infobox.classList.add('showinfo');
          this.keepInfoBoxInsideMap(infobox);
        }
      }
    }
  }

  /**
   * The infobox is placed by a fixed offset that centres it over its marker, which
   * pushes it outside the map for any marker near an edge -- on the search pages the
   * left part of the card then sits under the listing column and is unreadable.
   * Slide it back by however much it overhangs, keeping it anchored to its marker
   * everywhere there is room. A box wider or taller than the map is left alone:
   * nothing can be nudged into a container that cannot hold it.
   */
  private keepInfoBoxInsideMap(infobox: HTMLElement): void {
    if (!this.isBrowser || !this.radarMapContainer) return;

    const style = infobox.style;
    style.left = '';
    style.top = '';

    const map = this.radarMapContainer.nativeElement.getBoundingClientRect();
    const gap = 8;

    if (this.infoBoxPlacement === 'beside' && this.pageType === 'autorepair') {
      this.chooseInfoBoxSide(infobox, map, gap);
    }

    const box = infobox.getBoundingClientRect();
    if (!box.width || !box.height) return;   // hidden (d-none on mobile)

    const shift = (
      boxStart: number, boxEnd: number, mapStart: number, mapEnd: number
    ): number => {
      if (boxEnd - boxStart > mapEnd - mapStart) return 0;
      if (boxStart < mapStart + gap) return mapStart + gap - boxStart;
      if (boxEnd > mapEnd - gap) return mapEnd - gap - boxEnd;
      return 0;
    };

    const dx = shift(box.left, box.right, map.left, map.right);
    const dy = shift(box.top, box.bottom, map.top, map.bottom);
    const computed = getComputedStyle(infobox);
    if (dx) style.left = `${parseFloat(computed.left || '0') + dx}px`;
    if (dy) style.top = `${parseFloat(computed.top || '0') + dy}px`;
  }

  /**
   * `beside` placement: put the card on the side of the pin that can hold it. Right is
   * the default; the card flips left only when the right cannot fit it and the left
   * can. When neither side has room the right stays and the clamp below slides the card
   * back over the map. Measured against the marker container (the pin's box), not the
   * card, so a card already flipped left is judged by the same edges as one on the right.
   */
  private chooseInfoBoxSide(infobox: HTMLElement, map: DOMRect, gap: number): void {
    const anchor = infobox.parentElement?.getBoundingClientRect();
    const width = infobox.offsetWidth;
    if (!anchor || !width) return;
    const needed = width + gap * 2;
    const fitsRight = map.right - anchor.right >= needed;
    const fitsLeft = anchor.left - map.left >= needed;
    infobox.classList.toggle('place-left', !fitsRight && fitsLeft);
  }

  private repositionFrame: number | null = null;

  /**
   * Panning moves the marker under an open infobox, so the nudge above has to be
   * recomputed as the map moves or the card drifts back off the edge. The markers'
   * own transforms are written during the map's render pass, so measuring straight
   * from the event reads the previous frame's position -- wait for the next frame,
   * and coalesce the burst of events a single drag produces into one measurement.
   */
  private repositionOpenInfoBox = (): void => {
    if (!this.isBrowser || this.repositionFrame !== null) return;
    this.repositionFrame = requestAnimationFrame(() => {
      this.repositionFrame = null;
      if (!this.radarMapContainer) return;
      const open = this.radarMapContainer.nativeElement.querySelector('.infobx.showinfo');
      if (open) {
        this.keepInfoBoxInsideMap(open as HTMLElement);
      }
    });
  };

  /**
   * Get marker configuration based on page type
   */
  private getMarkerConfig(pageType: string) {
    // Default config used by all page types unless overridden
    const defaultConfig = {
      containerSelector: '.marker-cntr',
      markerSelector: '.nw-mrkr',
      containerActiveClass: 'active',
      markerActiveClass: 'activeMarker',
      markerIdPrefix: '#mrkerd_'
    };
    
    // Only define page-specific configs when they differ from default
    const pageSpecificConfigs: { [key: string]: Partial<typeof defaultConfig> } = {
      'city-parking': {
        containerSelector: '.cst-marker',
      },
      'gas': {
        containerSelector: '.gas-marker-cntr',
        markerSelector: '.gas-marker',
      }
    };
    
    // Merge: Start with default, then override with page-specific properties
    return {
      ...defaultConfig,
      ...(pageSpecificConfigs[pageType] || {})
    };
  }

  private mapWheelTarget: HTMLElement | null = null;
  private mapWheelHandler: ((event: WheelEvent) => void) | null = null;

  /** Map still swallows wheel events. Send them to the page instead. */
  private passWheelToPage(mapEl: HTMLElement) {
    if (!mapEl || this.mapWheelHandler) {
      return;
    }
    this.mapWheelTarget = mapEl;
    this.mapWheelHandler = (event: WheelEvent) => {
      event.preventDefault();
      event.stopPropagation();
      window.scrollBy(0, event.deltaY);
    };
    mapEl.addEventListener('wheel', this.mapWheelHandler, { passive: false, capture: true });
  }

  ngOnDestroy() {
    if (this.repositionFrame !== null) {
      cancelAnimationFrame(this.repositionFrame);
      this.repositionFrame = null;
    }
    if (this.mapWheelTarget && this.mapWheelHandler) {
      this.mapWheelTarget.removeEventListener('wheel', this.mapWheelHandler, true);
    }
    this.mapWheelTarget = null;
    this.mapWheelHandler = null;
    if (this.isBrowser && this.document) {
      this.document.removeEventListener('fullscreenchange', this.fullscreenChangeListener);
    }
    if (this.radarMap) {
      try {
        for (const ev of ['move', 'moveend', 'zoom', 'zoomend', 'resize']) {
          this.radarMap.off?.(ev, this.repositionOpenInfoBox);
        }
        this.clearMarkers();
        this.radarMap.remove();
      } catch (e) {
        console.error('Error destroying Radar map:', e);
      }
    }
  }
}
